import { useState, useCallback } from "react";
import jsPDF from "jspdf";

interface CVData {
  language: "fr" | "en";
  translations: {
    role: string;
    description: string;
    skillsTitle: string;
    experienceTitle: string;
    certificationsTitle: string;
    educationTitle: string;
    languagesTitle: string;
    skills: { title: string; description: string }[];
    experiences: { title: string; company: string; period: string; description: string; missions: string[] }[];
    edu1Title: string;
    edu1Subtitle: string;
    edu1Level: string;
    edu2Title: string;
    edu2Level: string;
    french: string;
    frenchLevel: string;
    english: string;
    englishLevel: string;
  };
}

export const useAtsPdfGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateAtsPdf = useCallback(async (data: CVData, filename: string) => {
    setIsGenerating(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // ========== ATS METADATA (Critical for parsing) ==========
      pdf.setProperties({
        title: `CV - Aurelia Ravelo - ${data.translations.role}`,
        subject: `Curriculum Vitae - ${data.translations.role}`,
        author: "Aurelia Ravelo",
        keywords: "Chef de Projet Digital, E-commerce, CRM, Shopify, Marketing Digital, Luxe, Project Manager, Digital, SEO, UX",
        creator: "Aurelia Ravelo CV Generator",
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const marginLeft = 18;
      const marginRight = 18;
      const contentWidth = pageWidth - marginLeft - marginRight;
      let y = 18;

      // ATS-Friendly Colors: High contrast, simple
      const black = [0, 0, 0] as const;
      const darkGray = [51, 51, 51] as const;
      const mediumGray = [102, 102, 102] as const;

      // Helper: check page break
      const checkPageBreak = (neededSpace: number) => {
        if (y + neededSpace > pageHeight - 18) {
          pdf.addPage();
          y = 18;
        }
      };

      // Helper: draw section header (ATS-friendly with clear separators)
      const drawSectionHeader = (title: string) => {
        checkPageBreak(12);
        y += 4;
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...black);
        pdf.text(title.toUpperCase(), marginLeft, y);
        y += 1;
        // Simple underline for clear section separation
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.4);
        pdf.line(marginLeft, y + 1, marginLeft + 50, y + 1);
        y += 5;
      };

      // Helper: add wrapped text with proper line height
      const addWrappedText = (
        text: string, 
        x: number, 
        maxWidth: number, 
        fontSize: number, 
        color: readonly [number, number, number], 
        fontStyle: "normal" | "bold" = "normal",
        lineHeight: number = 1.4
      ) => {
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", fontStyle);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line: string) => {
          checkPageBreak(fontSize * 0.5);
          pdf.text(line, x, y);
          y += fontSize * 0.35 * lineHeight;
        });
      };

      // ========== HEADER SECTION (Clean, parseable) ==========
      // Full name - largest, clear
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...black);
      pdf.text("AURELIA RAVELO", marginLeft, y);
      y += 8;
      
      // Job title - immediately under name (critical for ATS matching)
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkGray);
      pdf.text(data.translations.role.toUpperCase(), marginLeft, y);
      y += 7;

      // Contact information - each on clear format
      pdf.setFontSize(9);
      pdf.setTextColor(...mediumGray);
      
      // Email (plain text, no hyperlinks for ATS)
      pdf.text("Email: aurelia.ravelo@outlook.fr", marginLeft, y);
      y += 4;
      
      // Phone
      pdf.text(data.language === "fr" ? "Téléphone: +33 6 14 06 77 09" : "Phone: +33 6 14 06 77 09", marginLeft, y);
      y += 4;
      
      // LinkedIn (plain URL)
      pdf.text("LinkedIn: linkedin.com/in/aurelia-ravelo-a9ab15196", marginLeft, y);
      y += 4;
      
      // Location
      pdf.text(data.language === "fr" ? "Localisation: Paris, France" : "Location: Paris, France", marginLeft, y);
      y += 3;

      // Clear separator line
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(marginLeft, y + 2, pageWidth - marginRight, y + 2);
      y += 8;

      // ========== PROFESSIONAL SUMMARY ==========
      drawSectionHeader(data.language === "fr" ? "PROFIL PROFESSIONNEL" : "PROFESSIONAL SUMMARY");
      addWrappedText(data.translations.description, marginLeft, contentWidth, 9.5, darkGray, "normal", 1.5);
      y += 4;

      // ========== CORE COMPETENCIES / KEYWORDS ==========
      drawSectionHeader(data.language === "fr" ? "COMPETENCES CLES" : "CORE COMPETENCIES");
      
      // ATS Keywords - Critical: These are what ATS scans for
      const atsKeywords = [
        // Digital & E-commerce
        "E-commerce", "Shopify", "Magento", "PrestaShop", "WooCommerce",
        // CRM & Marketing
        "CRM", "Salesforce", "HubSpot", "Marketing Digital", "Email Marketing", "SEO", "SEA", "Google Analytics",
        // Project Management
        "Gestion de Projet", "Project Management", "JIRA", "Trello", "Agile", "Scrum",
        // Industry
        "Luxe", "Luxury", "Retail", "B2B", "B2C",
        // Tools
        "Microsoft Office", "Excel", "PowerPoint", "Tableau", "Photoshop", "Figma",
        // Soft Skills
        "Leadership", "Communication", "Coordination", "Analyse"
      ];
      
      // Display keywords in a scannable format
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkGray);
      
      // Split into rows for better readability
      const keywordsPerRow = 6;
      for (let i = 0; i < atsKeywords.length; i += keywordsPerRow) {
        const rowKeywords = atsKeywords.slice(i, i + keywordsPerRow).join("  |  ");
        checkPageBreak(5);
        pdf.text(rowKeywords, marginLeft, y);
        y += 4;
      }
      y += 3;

      // ========== SKILLS DETAIL ==========
      drawSectionHeader(data.translations.skillsTitle.toUpperCase());
      
      data.translations.skills.forEach((skill) => {
        checkPageBreak(10);
        
        // Skill title
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...black);
        pdf.text("• " + skill.title, marginLeft, y);
        y += 4;
        
        // Skill description
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...mediumGray);
        pdf.setFontSize(8.5);
        const descLines = pdf.splitTextToSize(skill.description, contentWidth - 5);
        descLines.forEach((line: string) => {
          pdf.text(line, marginLeft + 3, y);
          y += 3.2;
        });
        y += 2;
      });
      y += 2;

      // ========== PROFESSIONAL EXPERIENCE ==========
      drawSectionHeader(data.translations.experienceTitle.toUpperCase());

      data.translations.experiences.forEach((exp, index) => {
        checkPageBreak(25);
        
        // Job Title - Bold, prominent
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...black);
        pdf.text(exp.title, marginLeft, y);
        y += 4.5;
        
        // Company name
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...darkGray);
        pdf.text(exp.company, marginLeft, y);
        
        // Period - right aligned
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...mediumGray);
        const periodWidth = pdf.getTextWidth(exp.period);
        pdf.text(exp.period, pageWidth - marginRight - periodWidth, y);
        y += 5;

        // Job description
        pdf.setFontSize(8.5);
        pdf.setTextColor(...darkGray);
        const descLines = pdf.splitTextToSize(exp.description, contentWidth);
        descLines.forEach((line: string) => {
          checkPageBreak(4);
          pdf.text(line, marginLeft, y);
          y += 3.2;
        });
        y += 2;

        // Key achievements/missions with bullet points
        exp.missions.forEach((mission) => {
          checkPageBreak(6);
          pdf.setFontSize(8.5);
          pdf.setTextColor(...darkGray);
          
          // Bullet point
          pdf.text("•", marginLeft, y);
          
          // Mission text
          const missionLines = pdf.splitTextToSize(mission, contentWidth - 6);
          missionLines.forEach((line: string, lineIdx: number) => {
            pdf.text(line, marginLeft + 4, y);
            if (lineIdx < missionLines.length - 1) {
              y += 3.2;
              checkPageBreak(4);
            }
          });
          y += 4;
        });
        
        // Add space between experiences
        if (index < data.translations.experiences.length - 1) {
          y += 4;
        }
      });

      // ========== EDUCATION ==========
      checkPageBreak(30);
      drawSectionHeader(data.language === "fr" ? "DIPLÔMES" : "EDUCATION");

      // Education 1 - NEOMA Business School
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...black);
      pdf.text("NEOMA Business School", marginLeft, y);
      y += 4;
      
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkGray);
      pdf.setFontSize(8.5);
      const edu1Subtitle = data.language === "fr" 
        ? "MSc Luxury Marketing • Parcours International • Master 2 (2023)"
        : "MSc Luxury Marketing • International Track • Master 2 (2023)";
      pdf.text(edu1Subtitle, marginLeft, y);
      y += 4;

      // ========== LANGUAGES ==========
      checkPageBreak(20);
      drawSectionHeader(data.translations.languagesTitle.toUpperCase());

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...black);
      pdf.text(data.translations.french + ":", marginLeft, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkGray);
      pdf.text(" " + data.translations.frenchLevel, marginLeft + pdf.getTextWidth(data.translations.french + ":"), y);
      y += 5;

      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...black);
      pdf.text(data.translations.english + ":", marginLeft, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...darkGray);
      pdf.text(" " + data.translations.englishLevel, marginLeft + pdf.getTextWidth(data.translations.english + ":"), y);

      // ========== FOOTER - Page numbers for multi-page ==========
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(...mediumGray);
        pdf.text(
          `Page ${i}/${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      }

      // Save
      pdf.save(filename);
    } catch (error) {
      console.error("Error generating ATS PDF:", error);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return { generateAtsPdf, isGenerating };
};
